import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import AuthScreen from './components/AuthScreen';
import Dashboard from './components/Dashboard';
import ProjectBuilder from './components/ProjectBuilder';
import DeployPanel from './components/DeployPanel';
import LogViewer from './components/LogViewer';
import AgentChat from './components/AgentChat';
import AccountPanel from './components/AccountPanel';
import SupportCenter from './components/SupportCenter';
import ProjectCenter from './components/ProjectCenter';
import PublishingCenter from './components/PublishingCenter';
import ConnectionsPanel from './components/ConnectionsPanel';
import CustomDomainPanel from './components/CustomDomainPanel';
import ErrorBoundary from './components/ErrorBoundary';
import SideMenu, { MENU_ICONS } from './components/SideMenu';
import { ensureVisit, startNewEntry, firstNameFrom, greetingLine } from './welcome/welcomeVisit';
import { greetingText } from './welcome/greetings';
import { themeStyle } from './welcome/themes';
import { cacheTenant, readCachedTenant, clearCachedTenant, verifySession } from './session/sessionRestore';
import { api } from './api';
import { useLocale } from './i18n/LocaleContext';
import './styles/app.css';

// Task status is intentionally NOT derived from this channel. AgentChat/ProjectBuilder keep
// their existing authenticated GET polling as the durable source of truth, so a temporary
// realtime transport problem can never stall or restart a build. Socket.IO is best-effort UI
// enrichment only (logs/progress/credits). Keep retries bounded and increasingly spaced so a
// flaky mobile network or Hosting -> Cloud Run proxy cannot create a 400/429 reconnect storm.
const SOCKET_OPTIONS = {
  transports: ['polling'],
  upgrade: false,
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 2000,
  reconnectionDelayMax: 30000,
  randomizationFactor: 0.5,
  timeout: 10000,
  closeOnBeforeunload: true
};

function App() {
  const { locale, preference, setLocale, t } = useLocale();
  // A stored token plus the cached basic profile render the app immediately (see
  // session/sessionRestore.js); /api/auth/me is verified in the background.
  const [tenant, setTenant] = useState(() => (api.getToken() ? readCachedTenant() : null));
  const [authChecked, setAuthChecked] = useState(() => Boolean(api.getToken() && readCachedTenant()));
  const [sessionUnreachable, setSessionUnreachable] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [logs, setLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [credits, setCredits] = useState(null);
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(() => {
    try { return window.localStorage.getItem('codevoyage.profile-image') || ''; } catch (_) { return ''; }
  });
  const [profileImageError, setProfileImageError] = useState('');
  const profileInputRef = useRef(null);

  // On load, verify any stored token is still valid rather than trusting its presence blindly.
  // Only a 401/403 signs out; a busy or starting server is retried a bounded number of times.
  const [sessionCheck, setSessionCheck] = useState(0);
  useEffect(() => {
    const token = api.getToken();
    if (!token) { setAuthChecked(true); return undefined; }
    let cancelled = false;
    setSessionUnreachable(false);
    verifySession({ me: () => api.me() }).then(outcome => {
      if (cancelled) return;
      if (outcome.status === 'valid') { setTenant(outcome.tenant); cacheTenant(outcome.tenant); }
      else if (outcome.status === 'rejected') { api.setToken(null); clearCachedTenant(); setTenant(null); }
      else if (!readCachedTenant()) setSessionUnreachable(true);
      setAuthChecked(true);
    });
    return () => { cancelled = true; };
  }, [sessionCheck]);

  const refreshCredits = useCallback(() => {
    api.credits().then(res => setCredits(res.credits)).catch(() => {});
  }, []);

  // Personal welcome screen: one greeting/theme per new entry (welcome/welcomeVisit.js).
  const [welcomeVisit, setWelcomeVisit] = useState(null);
  const [isWelcome, setIsWelcome] = useState(false);
  const handleAuthenticated = useCallback((authenticatedTenant) => {
    startNewEntry(); // signing in is a new entry
    cacheTenant(authenticatedTenant);
    setTenant(authenticatedTenant);
  }, []);
  const handleTenantUpdated = useCallback((updated) => {
    cacheTenant(updated);
    setTenant(updated);
  }, []);

  const handleLogout = useCallback(() => {
    startNewEntry();
    clearCachedTenant();
    setWelcomeVisit(null);
    api.setToken(null);
    setTenant(null);
    setLogs([]);
    setTasks([]);
  }, []);

  // Side menu navigation. A chat action (new chat, all chats, delete, share, my app) opens the
  // chat and is carried out there exactly once - see the `command` prop of AgentChat.
  const menuButtonRef = useRef(null);
  const [chatCommand, setChatCommand] = useState(null);
  const closeMenu = useCallback(() => {
    setHeaderMenuOpen(false);
    setTimeout(() => menuButtonRef.current?.focus(), 0);
  }, []);
  const navigate = useCallback((tab) => {
    setActiveTab(tab);
    setHeaderMenuOpen(false);
  }, []);
  const runChatCommand = useCallback((type) => {
    setChatCommand({ type, nonce: Date.now() });
    setActiveTab('chat');
    setHeaderMenuOpen(false);
  }, []);
  const clearChatCommand = useCallback(() => setChatCommand(null), []);

  const handleProfileImageChange = useCallback((event) => {
    const [file] = event.target.files || [];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setProfileImageError(t('app.profileImageTypeError'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setProfileImageError(t('app.profileImageSizeError'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      try {
        window.localStorage.setItem('codevoyage.profile-image', dataUrl);
        setProfileImage(dataUrl);
        setProfileImageError('');
      } catch (_) {
        setProfileImageError(t('app.profileImageStorageError'));
      }
    };
    reader.onerror = () => setProfileImageError(t('app.profileImageReadError'));
    reader.readAsDataURL(file);
  }, [t]);

  const removeProfileImage = useCallback(() => {
    try { window.localStorage.removeItem('codevoyage.profile-image'); } catch (_) {}
    setProfileImage('');
    setProfileImageError('');
  }, []);

  useEffect(() => {
    if (!tenant?.id) return undefined;
    let cancelled = false;
    ensureVisit({ tenantId: tenant.id, fetchNext: visitId => api.welcomeNext(visitId).then(res => res.welcome) })
      .then(visit => { if (!cancelled) setWelcomeVisit(visit); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tenant?.id]);

  useEffect(() => {
    if (!tenant) return undefined;
    refreshCredits();

    // Production runs behind Firebase Hosting's rewrite to Cloud Run, which does not
    // reliably support WebSocket upgrades. Keep realtime on HTTP polling, but make that
    // auxiliary channel deliberately conservative: task polling remains authoritative and
    // bounded backoff prevents transient proxy/network failures from hammering /socket.io/.
    const s = io(api.API_URL, { ...SOCKET_OPTIONS, auth: { token: api.getToken() } });
    setSocket(s);

    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));
    s.on('connect_error', (err) => {
      setConnected(false);
      // Token invalid/expired mid-session - force re-auth rather than retrying a request that
      // can never succeed. Non-auth transport errors are allowed to follow the bounded policy
      // above; the build itself keeps progressing through its independent task polling.
      if (err.message?.includes('token')) handleLogout();
    });
    s.on('task_log', (log) => setLogs(prev => [...prev, log]));
    s.on('task_completed', (data) => {
      setTasks(prev => [...prev, data]);
      refreshCredits();
    });
    s.on('credits_changed', (data) => setCredits(data.credits));

    return () => s.disconnect();
  }, [tenant, refreshCredits, handleLogout]);

  if (!authChecked) return <div className="app-loading">{t('app.loading')}</div>;
  if (!tenant && sessionUnreachable) {
    return (
      <div className="app-loading" role="alert">
        <p>{t('app.serverUnreachable')}</p>
        <button type="button" className="btn" onClick={() => { setAuthChecked(false); setSessionCheck(value => value + 1); }}>{t('app.retry')}</button>
      </div>
    );
  }
  if (!tenant) return <AuthScreen onAuthenticated={handleAuthenticated} />;

  // Every screen that used to be a horizontal tab is now reached from the side menu
  // (SideMenu.jsx); the tab ids, and so every activeTab === ... check below, are unchanged.
  const sectionTitles = {
    dashboard: t('app.dashboard'), projects: t('app.projects'), builder: t('app.builder'), publishing: t('app.publishing'),
    connections: t('app.connections'), 'custom-domain': t('app.customDomain'), deploy: t('app.deploy'), logs: t('app.logs'),
    account: t('app.account'), support: t('app.support')
  };

  const profileInitial = String(tenant.email || 'C').trim().charAt(0).toUpperCase() || 'C';
  const welcomeLocale = locale === 'en' ? 'en' : 'he';
  const welcome = {
    line: greetingLine(firstNameFrom(tenant), welcomeLocale),
    phrase: greetingText(welcomeVisit?.greeting ?? 0, welcomeLocale),
    ready: Boolean(welcomeVisit)
  };
  const showWelcome = activeTab === 'chat' && isWelcome;

  return (
    <div className={`app app-tab-${activeTab}${showWelcome ? ' is-welcome' : ''}`} style={showWelcome ? themeStyle(welcomeVisit?.theme ?? 0) : undefined}>
      <header className="app-header">
        <div className="header-main">
          <div className="header-left">
            <button ref={menuButtonRef} type="button" className="header-menu-button" onClick={() => setHeaderMenuOpen(true)} aria-label={t('app.openMenu')} aria-expanded={headerMenuOpen} aria-controls="client-side-menu">
              <span /><span /><span />
            </button>
            <button type="button" className="header-brand" onClick={() => navigate('chat')} aria-label={t('app.chat')} dir="ltr">
              Code<span>Voyage</span>
            </button>
          </div>
          <div className="header-right">
            <button type="button" className="header-credits" onClick={() => navigate('account')} aria-label={`${t('menu.myCredits')}: ${credits ?? '—'}`} title={t('menu.myCredits')}>
              <span className="header-credits-icon" aria-hidden="true">{MENU_ICONS.coins}</span>
              <span className="header-credits-value">{credits ?? '—'}</span>
            </button>
            <button type="button" className="profile-avatar" onClick={() => navigate('account')} aria-label={t('menu.profile')} title={tenant.email}>
              {profileImage ? <img src={profileImage} alt={t('app.profileImageAlt')} /> : <span aria-hidden="true">{profileInitial}</span>}
            </button>
            <input ref={profileInputRef} className="sr-only" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleProfileImageChange} tabIndex={-1} aria-hidden="true" />
          </div>
        </div>
      </header>

      <SideMenu
        open={headerMenuOpen}
        onClose={closeMenu}
        t={t}
        dir={locale === 'he' ? 'rtl' : 'ltr'}
        tenant={tenant}
        credits={credits}
        profileImage={profileImage}
        profileInitial={profileInitial}
        profileImageError={profileImageError}
        onPickProfileImage={() => profileInputRef.current?.click()}
        onRemoveProfileImage={removeProfileImage}
        activeTab={activeTab}
        onNavigate={navigate}
        onChatCommand={runChatCommand}
        preference={preference}
        onSetLocale={setLocale}
        connected={connected}
        onLogout={handleLogout}
      />

      {activeTab !== 'chat' && (
        <div className="section-bar">
          <button type="button" className="section-back" onClick={() => navigate('chat')}>
            <span aria-hidden="true">{MENU_ICONS.back}</span>{t('menu.backToChat')}
          </button>
          <h2>{sectionTitles[activeTab] || ''}</h2>
        </div>
      )}

      <main className="main-content">
        {/* key={activeTab} remounts the boundary on every screen switch, so recovering from
            a crash is as simple as opening a different screen - no separate reset wiring. */}
        <ErrorBoundary key={activeTab}>
          {activeTab === 'chat' && <AgentChat onCreditsChanged={refreshCredits} socket={socket} onNavigateToAccount={() => navigate('account')} command={chatCommand} onCommandHandled={clearChatCommand} onNavigate={navigate} welcome={welcome} onWelcomeChange={setIsWelcome} />}
          {activeTab === 'dashboard' && <Dashboard tasks={tasks} logs={logs} />}
          {activeTab === 'projects' && <ProjectCenter onNavigateToBuilder={() => navigate('builder')} />}
          {activeTab === 'publishing' && <PublishingCenter tenant={tenant} />}
          {activeTab === 'connections' && <ConnectionsPanel onNavigateToPublishing={() => navigate('publishing')} />}
          {activeTab === 'custom-domain' && <CustomDomainPanel />}
          {activeTab === 'builder' && <ProjectBuilder onBuildStarted={refreshCredits} onNavigateToAccount={() => navigate('account')} />}
          {activeTab === 'deploy' && <DeployPanel onNavigateToBuilder={() => navigate('builder')} />}
          {activeTab === 'logs' && <LogViewer logs={logs} />}
          {activeTab === 'account' && <AccountPanel tenant={tenant} credits={credits} onCreditsChanged={refreshCredits} onTenantUpdated={handleTenantUpdated} />}
          {activeTab === 'support' && <SupportCenter />}
        </ErrorBoundary>
      </main>

      <footer className="app-footer">
          <span>CodeVoyage.ai <i /> {t('app.footer')}</span>
      </footer>
    </div>
  );
}

export default App;
